import * as React from 'react';
import SvgEditService from '@/services/svg-edit-service';
import ZenDataService from '@/services/zen-data-service';
import FontsService from '@/services/fonts-service';
import PanelsService from '@/services/panels-service';
import PDTagEditorTextbox from './TagEditorTextbox';
import { SvgAnnotations } from '../../svg-annotations';
import { isPDComponentElement } from '@/common/pdUtils';
import styles from '../pdTagEditor.module.scss';
import { InputUnits } from '../../input-units';

const langStrings = {
    multicomponentSeparator: "If set, this component when repeating will be split into separate blocks determined by this value. This value will then also be used as a header label for these blocks. For example, splitting a colour swatch based on it's component name.",
    multicomponentLabelTemplate: "A template used to generate the label name. Use '%%' to indicate where the multicomponent split value will be substituted in. For example, a template of 'Component %%' for a job with both 'Back' and 'Front' components will ouput 'Component Back' and 'Component Front'.",
    multicomponentSorting: "How the split blocks are ordered. Ascending and descending will act alphabetically (So with ascending, the 'Inner' component will always be before 'Outer'), while 'Default' will use whatever order is stored in Zen, meaning component order could differ between jobs.",
    multicomponentLabelFont: "Which font family to use for the labels used for each multicomponent block.",
    multicomponentLabelSize: "The point size for the multicomponent block label.",
    multicomponentLabelMargin: "The spacing between the multicomponent block label and the actual repeating components for the multicomponent block.",
    multicomponentBlockMargin: "The spacing between the last component in the current multicomponent block and the label of the next block, if any."
};

enum PETagAttribute {
    Content = "zaa:content-tag",
    Fill = "zaa:fill-tag",
    Stroke = "zaa:stroke-tag",
    Component = "zaa:component-tag",
}

enum PEMappingFlags {
    None = 0,
    AllowDateSelection = 1 << 0,
}

type TagFilterMode = "All" | "Normal" | "InnerComponent" | "Function";
type MulticomponentSorting = "default" | "asc" | "desc";

interface State {
    selectedAttrib: PETagAttribute;
    selectedAttribWasOnText: boolean;
    selectedTagFilter: TagFilterMode;
    selectedTag: string;
    selectedTagArgs: any[];
    selectedTabIndex: number;

    multicompSeparator: string;
    multicompLabelTemplate: string;
    multicompSorting: MulticomponentSorting;
    multicompLabelFont: string;
    multicompLabelSize: number;
    multicompLabelMargin: number;
    multicompBlockMargin: number;
}

interface IAttrMap {
    [key: string]: { attr: string, format?: (v: any) => string }
}


export class PDTagEditor_Tagging extends React.Component<{}, State> {
    private stateKeyToAttrMap: IAttrMap = {
        "multicompSeparator": { attr: "pe-multicomp-split", format: (v) => v === "~" ? null : v },
        "multicompLabelTemplate": { attr: "pe-multicomp-labeltemplate" },
        "multicompSorting": { attr: "pe-multicomp-sort" },
        "multicompLabelFont": { attr: "pe-multicomp-label-font" },
        "multicompLabelSize": { attr: "pe-multicomp-label-size" },
        "multicompLabelMargin": { attr: "pe-multicomp-label-margin" },
        "multicompBlockMargin": { attr: "pe-multicomp-section-margin" },
    }

    private getDefaultState = () => {
        const state: State = {
            selectedAttrib: PETagAttribute.Content,
            selectedAttribWasOnText: true,
            selectedTagFilter: "All",
            selectedTag: "",
            selectedTagArgs: [],
            selectedTabIndex: 0,

            multicompSeparator: "~",
            multicompLabelTemplate: "%%",
            multicompSorting: "asc",
            multicompLabelFont: "Helvetica",
            multicompLabelSize: 7,
            multicompLabelMargin: 1.5875,
            multicompBlockMargin: 1.5875,
        };

        return state;
    }

    state = this.getDefaultState();

    public svgEditService = SvgEditService.Get(this, () => this.selectedElementChanged());
    public zenDataService = ZenDataService.Get(this);
    public fontsService = FontsService.Get(this);
    public panelService = PanelsService.Get(this, () => this.panelDataUpdated());

    private tagField: React.RefObject<PDTagEditorTextbox> = React.createRef();

    public componentDidMount() {
        this.selectedElementChanged();
    }

    public componentWillUnmount() {
        this.svgEditService.UnregisterComponent(this);
        this.zenDataService.UnregisterComponent(this);
        this.fontsService.UnregisterComponent(this);
        this.panelService.UnregisterComponent(this);
    }

    private selectedElementChanged = () => {
        if (this.svgEditService.selectedElements.length !== 1) {
            return;
        }

        const el = this.svgEditService.selectedElements[0];
        const tagName = el.tagName.toLowerCase();

        this.setState(prevState => {
            if (isPDComponentElement(el)) {
                this.setSelectedTagPart(PETagAttribute.Component, prevState.selectedAttrib === PETagAttribute.Content || prevState.selectedAttribWasOnText);
                
                const newState = { ...prevState };
                const defaultState = this.getDefaultState();

                newState.selectedTag = el.getAttribute(PETagAttribute.Component) ?? "~";
                newState.multicompSeparator = el.getAttribute("pe-multicomp-split") ?? defaultState.multicompSeparator;
                newState.multicompSorting = (el.getAttribute("pe-multicomp-sort") ?? defaultState.multicompSorting) as MulticomponentSorting;
                newState.multicompLabelFont = el.getAttribute("pe-multicomp-label-font") ?? defaultState.multicompLabelFont;
                newState.multicompLabelSize = Number(el.getAttribute("pe-multicomp-label-size") ?? defaultState.multicompLabelSize);
                newState.multicompLabelMargin = Number(el.getAttribute("pe-multicomp-label-margin") ?? defaultState.multicompLabelMargin);
                newState.multicompBlockMargin = Number(el.getAttribute("pe-multicomp-section-margin") ?? defaultState.multicompBlockMargin);

                return newState;
            } else {
                let selectedTabIndex = prevState.selectedTabIndex;
                if (selectedTabIndex != 0 && selectedTabIndex != 3) {
                    selectedTabIndex = 0;
                }

                if (tagName !== "text") {
                    if (prevState.selectedAttrib === PETagAttribute.Content || prevState.selectedAttrib === PETagAttribute.Component) {
                        this.setSelectedTagPart(PETagAttribute.Fill, true);
                    }
                } else if (prevState.selectedAttribWasOnText || prevState.selectedAttrib === PETagAttribute.Component) {
                    this.setSelectedTagPart(PETagAttribute.Content, true);
                }

                return { selectedTabIndex };
            }
        }, () => InputUnits.UpdateAllInputs());
    }

    private panelDataUpdated = () => {
        if (this.state.selectedTag === "" && this.panelService.panelTagMappings.length > 0) {
            // Force an update for our currently selected mapping.
            this.setSelectedTagFilter();
        }
    }

    private setSelectedTagPart = (newPart: PETagAttribute, wasAutoChangeFromText: boolean = false) => {
        this.setState({
            selectedAttrib: newPart,
            selectedAttribWasOnText: wasAutoChangeFromText,
        });
        setTimeout(() => this.tagField.current?.onSelectionChanged());
    }

    private setSelectedTagFilter = (ev?: React.ChangeEvent<HTMLInputElement>) => {
        let updateObj = {
            selectedTagFilter: this.state.selectedTagFilter,
            selectedTag: this.state.selectedTag,
        };

        if (ev) {
            updateObj.selectedTagFilter = ev.target.value as TagFilterMode;
        }

        let mappings = this.getValidTagMappings(updateObj.selectedTagFilter);
        if (mappings.length > 0 && !mappings.some(m => m.tag === updateObj.selectedTag)) {
            // We're not currently selecting a valid mapping. Reset to first of the valid bunch.
            updateObj.selectedTag = mappings[0].tag;
        }

        this.setState(updateObj);
    }

    private setSelectedTag = (ev: React.ChangeEvent<HTMLSelectElement>) => {
        let selectedTag = this.getSelectedTag(ev.target.value);
        let selectedTagArgs = [];

        if (selectedTag.args) {
            for (let a of selectedTag.args) {
                selectedTagArgs.push(a.initialValue);
            }
        }

        this.setState({
            selectedTag: ev.target.value,
            selectedTagArgs: selectedTagArgs,
        });
    }

    private setSelectedComponentTag = (ev: React.ChangeEvent<HTMLSelectElement>) => {
        const value = ev.target.value;
        this.svgEditService.canvas.changeSelectedAttribute(PETagAttribute.Component, value !== "~" ? value : null, null);

        this.setState({
            selectedTag: ev.target.value,
            selectedTagArgs: [],
        });

        this.svgEditService.ElementStateHasChanged();
    }

    private updateSelectedTagArgs(value: string, index: number) {
        let args = [];

        for (let a of this.state.selectedTagArgs) {
            args.push(a);
        }

        args[index] = value;

        this.setState({
            selectedTagArgs: args,
        });
    }

    private insertSelectedTag = (isOverwrite: boolean) => {
        const tagMapping: PanelDesigner.IMapping = this.getSelectedTag();

        if (!tagMapping?.tag) {
            return;
        }

        const el = this.svgEditService.selectedElements[0];
        const isComponent = isPDComponentElement(el);

        let tagStr = tagMapping.tag;

        if (!isComponent) {
            tagStr = "${{" + tagStr;

            if (this.state.selectedTagArgs.length > 0) {
                tagStr += "(";

                for (let i = 0; i < this.state.selectedTagArgs.length; ++i) {
                    if (i > 0) {
                        tagStr += ", ";
                    }

                    tagStr += this.state.selectedTagArgs[i];
                }

                tagStr += ")";
            }

            tagStr += "}}";
        }

        this.tagField.current.insertTag(tagStr, isComponent || isOverwrite);
    }

    public getValidTagMappings = (filter?: string, flags: number = 0) => {
        filter = filter ?? this.state.selectedTagFilter;

        let mappings: PanelDesigner.IMapping[] = [];

        if (this.panelService.panelTagMappings.length > 0) {
            const el = this.svgEditService.selectedElements[0];
            const isSelectingComponent = isPDComponentElement(el);

            if (!isSelectingComponent) {
                switch (filter) {
                    case "Normal":
                        mappings = this.panelService.panelTagMappings
                            .filter(m => !m.tag.startsWith('&') && !m.tag.startsWith('$'));
                        break;

                    case "InnerComponent":
                        mappings = this.panelService.panelTagMappings
                            .filter(m => m.tag.startsWith('$'));
                        break;

                    case "Function":
                        mappings = this.panelService.panelTagMappings
                            .filter(m => m.tag.startsWith('&'));
                        break;

                    case "All":
                    default:
                        mappings = this.panelService.panelTagMappings;
                        break;
                }
            } else {
                mappings = this.panelService.panelTagMappings
                    .filter(m => m.componentsOnly);
            }

            let scopeFilter;
            if (this.state.selectedAttrib === PETagAttribute.Content) {
                scopeFilter = "textcontent";
            } else {
                scopeFilter = "colour";
            }

            // Remove out of scope functions
            mappings = mappings.filter(m => (!m.tag.startsWith('&') || (m.scope === "any" || m.scope === scopeFilter)));

            if ((flags & PEMappingFlags.AllowDateSelection) != 0) {
                mappings.splice(0, 0, {
                    name: "Current UTC Date/Time",
                    displayName: "Current UTC Date/Time",
                    description: "The current time in UTC",
                    tag: "CURRENT_DATETIME_UTC",
                    componentsOnly: false,
                    args: [],
                });

                mappings.splice(1, 0, {
                    name: "Current Job Date/Time",
                    displayName: "Current Job Date/Time",
                    description: "The current time in the Job's current timezone",
                    tag: "CURRENT_DATETIME_JOB",
                    componentsOnly: false,
                    args: [],
                });
            }
        }

        return mappings;
    }

    private getSelectedTag = (tagName?: string): PanelDesigner.IMapping => {
        tagName = tagName ?? this.state.selectedTag;

        for (const t of this.panelService.panelTagMappings) {
            if (t.tag === tagName) {
                return t;
            }
        }

        return null;
    }

    private setTextElementContentToCurrentTag = () => {
        const el = this.svgEditService.selectedElements[0];
        const isTextEl = el && el.tagName.toLowerCase() === "text";
        if (!isTextEl) {
            console.warn("Tried to set text content of a non-text element!");
            return;
        }

        const tagContent = el.getAttribute(this.state.selectedAttrib)?.trim() ?? "";
        if (tagContent.length == 0) {
            console.warn("Tried to set text content of a text element to an empty value!");
            return;
        }

        this.svgEditService.canvas.changeSelectedAttribute("#text", tagContent, null);
    }

    private untagSelectedElements() {
        this.svgEditService.canvas.changeSelectedAttribute(PETagAttribute.Component, null, null);
        this.svgEditService.canvas.changeSelectedAttribute(PETagAttribute.Content, null, null);
        this.svgEditService.canvas.changeSelectedAttribute(PETagAttribute.Fill, null, null);
        this.svgEditService.canvas.changeSelectedAttribute(PETagAttribute.Stroke, null, null);
        this.setState({});
        SvgAnnotations.UpdateAnnotations();
        setTimeout(() => this.tagField.current?.onSelectionChanged());
    }

    private onMulticomponentValueChanged = (key: string, value: string | number) => {
        const attrMap = this.stateKeyToAttrMap[key];
        this.svgEditService.canvas.changeSelectedAttribute(attrMap.attr, attrMap.format ? attrMap.format(value) : value, null);

        this.setState(prevState => ({ ...prevState, [key]: value }));
    }

    private elementAsAnyTags(el: SVGElement) {
        return el.hasAttribute(PETagAttribute.Component)
            || el.hasAttribute(PETagAttribute.Content)
            || el.hasAttribute(PETagAttribute.Fill)
            || el.hasAttribute(PETagAttribute.Stroke)
    }

    public render() {
        const el = this.svgEditService.selectedElements[0];
        const getID = (p: string) => `pd-tagEditor__${p}`;

        const capitalize = (str: string) => {
            if (str.length > 1) {
                return str[0].toUpperCase() + str.substring(1);
            }
        }

        const isTextEl = el && el.tagName.toLowerCase() === "text";
        const isComponentEl = isPDComponentElement(el);

        if (this.svgEditService.selectedElements.length > 1) {
            return <span>Too many elements are selected! You can only tag one element at a time!</span>;
        } else if (this.svgEditService.selectedElements.length == 0) {
            return <span>No elements selected</span>;
        }

        let mappings: PanelDesigner.IMapping[] = this.getValidTagMappings();
        let mappingsDisabled = false;

        if (this.panelService.panelTagMappings.length == 0) {
            mappings.push({
                name: "-- Loading --",
                displayName: "-- Loading --",
                description: "Please wait...",
                componentsOnly: false,
                isForComponents: false,
                tag: "",
                args: []
            });
            mappingsDisabled = true;
        }

        const selectedTag: PanelDesigner.IMapping = this.getSelectedTag();
        const selectedTagDescription: string = selectedTag?.description ?? (isComponentEl ? "This component isn't tagged and will act like a normal element." : "");
        const selectedTagArgs: PanelDesigner.IMappingArg[] = selectedTag?.args ?? [];
        const currentTagContent = el.getAttribute(this.state.selectedAttrib)?.trim() ?? "";

        let multiComponentSeparators: PanelDesigner.IMapping[] = [];
        if (isComponentEl && this.state.selectedTag) {
            switch (this.state.selectedTag.toUpperCase()) {
                case "ELEMENTP_ARTWORK_SWATCH":
                    multiComponentSeparators = this.panelService.panelTagMappings.filter(m => m.name.startsWith("Artwork Swatch ->"));
                    break;
                case "ELEMENTP_ARTWORK_BARCODES":
                    multiComponentSeparators = this.panelService.panelTagMappings.filter(m => m.name.startsWith("Barcodes ->"));
                    break;
                case "ELEMENTP_ARTWORK_BRAILLE_ITR":
                    multiComponentSeparators = this.panelService.panelTagMappings.filter(m => m.name.startsWith("Braille ->"));
                    break;
            }
        }

        const elScript = el.getAttribute("pe-script");
        const hasScript = elScript && elScript.trim().length > 0;

        return (
            <div>
                <h1>Element Tag Data</h1>
                <label htmlFor="componentTypeSelectDdl" className={styles.selectedPartDropdown}>
                    <div>
                        <span>Selected Element Part:</span>
                        <span className={styles.selectedPartComponentLabel} hidden={!isComponentEl}> Panel Component</span>
                    </div>
                    <select id="componentTypeSelectDdl" className="form-select" hidden={isComponentEl} value={this.state.selectedAttrib} onChange={ev => this.setSelectedTagPart(ev.target.value as PETagAttribute)}>
                        <option value={PETagAttribute.Content} hidden={!isTextEl}>Text Content</option>
                        <option value={PETagAttribute.Fill}>Fill Colour</option>
                        <option value={PETagAttribute.Stroke}>Stroke Colour</option>
                    </select>
                </label>

                {isComponentEl ? (
                    <>
                        <div className={styles.insertTagWrapper}>
                            <select name="panelComponentMappingSelect" className="form-select" value={this.state.selectedTag} onChange={ev => this.setSelectedComponentTag(ev)} disabled={mappingsDisabled}>
                                <option value={"~"}>None</option>
                                {mappings.map(mapping => {
                                    if (mapping?.tag) {
                                        return (
                                            <option key={mapping.displayName} value={mapping.tag} title={mapping.description}>{mapping.displayName}</option>
                                        );
                                    }
                                })}
                            </select>
                        </div>

                        <div className={styles.insertTagDescription}>
                            {selectedTagDescription}
                        </div>

                        {this.state.selectedTag && this.state.selectedTag !== "~" && (
                            <div>
                                <h2>Multicomponent Settings</h2>
                                <table>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <label htmlFor={getID("multicomp_separator")} title={langStrings.multicomponentSeparator}>
                                                    Multicomponent Separator
                                                </label>
                                            </td>
                                            <td>
                                                <select id={getID("multicomp_separator")} className="form-select" title={langStrings.multicomponentSeparator}
                                                    value={this.state.multicompSeparator} onChange={e => this.onMulticomponentValueChanged("multicompSeparator", e.target.value)}>
                                                    <option value={"~"}>- No Separator -</option>
                                                    {multiComponentSeparators.map((sepMapping) => {
                                                        if (sepMapping?.tag) {
                                                            return (
                                                                <option key={sepMapping.tag} value={sepMapping.tag}>{sepMapping.name}</option>
                                                            );
                                                        }
                                                    })}
                                                </select>
                                            </td>
                                        </tr>

                                        <tr hidden={this.state.multicompSeparator === null || this.state.multicompSeparator === "~"}>
                                            <td>
                                                <label htmlFor={getID("multicomp_labelTemplate")} title={langStrings.multicomponentLabelTemplate}>
                                                    Label Template
                                                </label>
                                            </td>
                                            <td>
                                                <div className="input-group">
                                                    <input type="text" id={getID("multicomp_labelTemplate")} className="form-control" title={langStrings.multicomponentLabelTemplate}
                                                        value={this.state.multicompLabelTemplate} onChange={e => this.onMulticomponentValueChanged("multicompLabelTemplate", e.target.value)} />
                                                </div>
                                            </td>
                                        </tr>

                                        <tr hidden={this.state.multicompSeparator === null || this.state.multicompSeparator === "~"}>
                                            <td>
                                                <label htmlFor={getID("multicomp_sorting")} title={langStrings.multicomponentSorting}>
                                                    Multicomponent Sorting
                                                </label>
                                            </td>
                                            <td>
                                                <select id={getID("multicomp_sorting")} className="form-select" title={langStrings.multicomponentSorting}
                                                    value={this.state.multicompSorting} onChange={e => this.onMulticomponentValueChanged("multicompSorting", e.target.value)}>
                                                    <option value="default">Default</option>
                                                    <option value="asc">Ascending</option>
                                                    <option value="desc">Descending</option>
                                                </select>
                                            </td>
                                        </tr>

                                        <tr hidden={this.state.multicompSeparator === null || this.state.multicompSeparator === "~"}>
                                            <td>
                                                <label htmlFor={getID("multicomp_labelfont")} title={langStrings.multicomponentLabelFont}>
                                                    Label Font
                                                </label>
                                            </td>
                                            <td>
                                                <select id={getID("multicomp_labelfont")} className="form-select" title={langStrings.multicomponentLabelFont}
                                                    value={this.state.multicompLabelFont} onChange={e => this.onMulticomponentValueChanged("multicompLabelFont", e.target.value)}>
                                                    {this.fontsService.fonts.map(font => {
                                                        if (font?.name) {
                                                            return (
                                                                <option key={font.name} value={font.name} style={{ fontFamily: font.name }}>{capitalize(font.name)}</option>
                                                            );
                                                        }

                                                        return null;
                                                    })}
                                                </select>
                                            </td>
                                        </tr>

                                        <tr hidden={this.state.multicompSeparator === null || this.state.multicompSeparator === "~"}>
                                            <td>
                                                <label htmlFor={getID("multicomp_labelsize")} title={langStrings.multicomponentSeparator}>
                                                    Label Size
                                                </label>
                                            </td>
                                            <td>
                                                <div className="input-group">
                                                    <input type="number" id={getID("multicomp_labelsize")} className="form-control" min={0} max={999999} step={0.1} title={langStrings.multicomponentLabelSize}
                                                        value={this.state.multicompLabelSize} onChange={e => this.onMulticomponentValueChanged("multicompLabelSize", e.target.valueAsNumber)} />
                                                    <span className="input-group-text">pt</span>
                                                </div>
                                            </td>
                                        </tr>

                                        <tr hidden={this.state.multicompSeparator === null || this.state.multicompSeparator === "~"}>
                                            <td>
                                                <label htmlFor={getID("multicomp_labelmargin")} title={langStrings.multicomponentLabelMargin}>
                                                    Label Margin
                                                </label>
                                            </td>
                                            <td>
                                                <InputUnits id={getID("multicomp_labelmargin")} min={0} max={999999} step={0.1} decimalPlaces={2} value={this.state.multicompLabelMargin} onChange={e => this.onMulticomponentValueChanged("multicompLabelMargin", e)} />
                                            </td>
                                        </tr>

                                        <tr hidden={this.state.multicompSeparator === null || this.state.multicompSeparator === "~"}>
                                            <td>
                                                <label htmlFor={getID("multicomp_blockmargin")} title={langStrings.multicomponentBlockMargin}>
                                                    Multicomponent Block Margin
                                                </label>
                                            </td>
                                            <td>
                                                <InputUnits id={getID("multicomp_blockmargin")} min={0} max={999999} step={0.1} decimalPlaces={2} value={this.state.multicompBlockMargin} onChange={e => this.onMulticomponentValueChanged("multicompBlockMargin", e)} />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <PDTagEditorTextbox ref={this.tagField} tagAttrib={this.state.selectedAttrib} lang="pdtag" />
                        <div className={styles.utilityButtons}>
                            <button type="button" className="pdButton" hidden={!isTextEl || currentTagContent.length == 0}
                                onClick={() => this.setTextElementContentToCurrentTag()}>
                                Set Element Text to Tag Value
                            </button>
                            <button type="button" className="pdButton" hidden={!this.elementAsAnyTags(el)}
                                onClick={() => this.untagSelectedElements()}>
                                Untag Selected Element
                            </button>
                        </div>

                        <div className="pdTagEditor__insertTagFilterWrapper pdRadioButtonGroup">
                            <input type="radio" id="tagFilter__All" name="tagFilter" value="All" checked={this.state.selectedTagFilter == "All"} onChange={ev => this.setSelectedTagFilter(ev)} />
                            <label className="pdRadioButton" htmlFor="tagFilter__All">All</label>

                            <input type="radio" id="tagFilter__Normal" name="tagFilter" value="Normal" checked={this.state.selectedTagFilter == "Normal"} onChange={ev => this.setSelectedTagFilter(ev)} />
                            <label className="pdRadioButton" htmlFor="tagFilter__Normal">Config</label>

                            <input type="radio" id="tagFilter__InnerComponent" name="tagFilter" value="InnerComponent" checked={this.state.selectedTagFilter == "InnerComponent"} onChange={ev => this.setSelectedTagFilter(ev)} />
                            <label className="pdRadioButton" htmlFor="tagFilter__InnerComponent">Component</label>

                            <input type="radio" id="tagFilter__Function" name="tagFilter" value="Function" checked={this.state.selectedTagFilter == "Function"} onChange={ev => this.setSelectedTagFilter(ev)} />
                            <label className="pdRadioButton" htmlFor="tagFilter__Function">Function</label>
                        </div>
                        <div className={styles.insertTagWrapper}>
                            <select name="panelTagMappingSelect" className="form-select" value={this.state.selectedTag} onChange={ev => this.setSelectedTag(ev)} disabled={mappingsDisabled}>
                                {mappings.map(mapping => (
                                    <option key={mapping.displayName} value={mapping.tag} title={mapping.description}>{mapping.displayName}</option>
                                ))}
                            </select>
                            <button type="button" className="pdButton" onClick={() => this.insertSelectedTag(false)}>Insert</button>
                            <button type="button" className="pdButton" onClick={() => this.insertSelectedTag(true)}>Replace</button>
                        </div>
                        <div className={styles.insertTagDescription}>
                            {selectedTagDescription}
                        </div>
                        {selectedTag?.obsoleteInDesignerVersion >= this.zenDataService.GetPanelDesignerVersion() && (
                            <div className={`alert alert-warning ${styles.taggingAlert}`} role="alert">
                                <strong>This tag is obsolete in this panel designer version!</strong>
                                <p>{selectedTag.obsoleteReason}</p>
                            </div>
                        )}
                        {hasScript && (
                            <div className={`alert alert-info ${styles.taggingAlert}`} role="alert">
                                <strong>The selected element has a script applied!</strong>
                                <p>This tag might be overridden or otherwise not applied due to this script.</p>
                            </div>
                        )}
                        <table className={styles.insertTagArgs}>
                            <tbody>
                                {selectedTagArgs.map((a, i) => (
                                    <tr key={`${a.displayName}_${a.type}`}>
                                        <td>
                                            <label htmlFor={getID(`insertTagArgs_${i}`)}> {a.displayName}</label>
                                        </td>
                                        <td>
                                            <PDFuncArgument arg={a} argIndex={i} mapping={selectedTag} value={this.state.selectedTagArgs[i]} parent={this} onChange={(av, ai) => this.updateSelectedTagArgs(av, ai)} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div >
        );
    }
}

interface PDFuncArgumentProps {
    arg: PanelDesigner.IMappingArg;
    argIndex: number;
    mapping: PanelDesigner.IMapping;
    value: any;
    onChange(newValue: any, argIndex: number): void;
    parent: PDTagEditor_Tagging;
}

interface PDFuncArgumentState {
    dropdownSelected: string;
}

const ARGUMENT_PLAINTEXT = "$$PLAINTEXT$$";

class PDFuncArgument extends React.Component<PDFuncArgumentProps, PDFuncArgumentState> {
    private GetDefaultState = () => {
        const s: PDFuncArgumentState = {
            dropdownSelected: ARGUMENT_PLAINTEXT
        };
        return s;
    }

    state = this.GetDefaultState();

    private onChange(newValue: any) {
        this.props.onChange(newValue, this.props.argIndex);
    }

    private onDropdownChange(newValue: any) {
        if (newValue !== ARGUMENT_PLAINTEXT) {
            this.onChange(`$<<${newValue}>>`);
        }

        this.setState({ dropdownSelected: newValue });
    }

    public render() {
        const getID = (p: string | number) => `pd-tagEditor__insertTagArgs_${p}`;

        const id = getID(this.props.argIndex);
        const suffix = this.props.arg.suffix && this.props.arg.suffix.length > 0 ? this.props.arg.suffix : null;

        let mappings: PanelDesigner.IMapping[] = this.props.parent.getValidTagMappings("All", this.props.mapping.flags);
        let mappingsDisabled = false;

        if (this.props.parent.panelService.panelTagMappings.length == 0) {
            mappings.push({
                name: "-- Loading --",
                displayName: "-- Loading --",
                description: "Please wait...",
                componentsOnly: false,
                tag: "",
                args: []
            });
            mappingsDisabled = true;
        }

        switch (this.props.arg.type) {
            case "number":
                return (
                    <div className="input-group">
                        <input id={id} type="number" className="form-control" value={this.props.value ?? 0} onChange={ev => this.onChange(ev.target.value)} />
                        {suffix && <span className="input-group-text">{suffix}</span>}
                    </div>
                );

            case "percentage":
                return (
                    <div className="input-group">
                        <input id={id} type="number" className="form-control" value={this.props.value ?? 0} min={0} max={100} step={0.1} onChange={ev => this.onChange(ev.target.value)} />
                        <span className="input-group-text">{suffix || '%'}</span>
                    </div>
                );

            case "tag":
            case "tagtext": {
                let tagMappings: PanelDesigner.IMapping[] = [...mappings];

                if (this.props.parent.panelService.panelTagMappings.length > 0) {
                    if (this.props.arg.type === "tag") {
                        if (this.props.value === "") {
                            tagMappings.splice(0, 0, {
                                name: "-- No Tag Selected --",
                                displayName: "-- No Tag Selected --",
                                description: "Please select a tag",
                                componentsOnly: false,
                                tag: "",
                                args: []
                            });
                        }
                    } else {
                        tagMappings.splice(0, 0, {
                            name: "-- Plain Text --",
                            displayName: "-- Plain Text --",
                            description: "Add plain, untagged text",
                            componentsOnly: false,
                            tag: "$$PLAINTEXT$$",
                            args: []
                        });
                    }
                }

                if (this.props.arg.type === "tagtext" && this.state.dropdownSelected === "$$PLAINTEXT$$") {
                    return (
                        <div>
                            <div className="input-group">
                                <select className="form-select" value={this.state.dropdownSelected} onChange={ev => this.onDropdownChange(ev.target.value)} disabled={mappingsDisabled}>
                                    {tagMappings.map(mapping => (
                                        <option key={mapping.tag} value={mapping.tag} title={mapping.description}>{mapping.displayName}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="input-group">
                                <input id={id} type="text" className="form-control" value={this.props.value ?? ""} onChange={ev => this.onChange(ev.target.value)} />
                                {suffix && <span className="input-group-text">{suffix}</span>}
                            </div>
                        </div>
                    );
                }

                return (
                    <div>
                        <div className="input-group">
                            <select className="form-select" value={this.state.dropdownSelected} onChange={ev => this.onDropdownChange(ev.target.value)} disabled={mappingsDisabled}>
                                {tagMappings.map(mapping => (
                                    <option key={mapping.tag} value={mapping.tag} title={mapping.description}>{mapping.displayName}</option>
                                ))}
                            </select>
                            {suffix && <span className="input-group-text">{suffix}</span>}
                        </div>
                    </div>
                );
            }
            case "text":
            default:
                return (
                    <div className="input-group">
                        <input id={id} type="text" className="form-control" value={this.props.value ?? ""} onChange={ev => this.onChange(ev.target.value)} />
                        {suffix && <span className="input-group-text">{suffix}</span>}
                    </div>
                );
        }
    }
}
